import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { AnimatedLotus } from "../components/AnimatedLotus";
import { BecomeLogo } from "../components/BecomeLogo";
import { VideoMockupBackground } from "../components/VideoBackground";
import { NiaMockup } from "../components/NiaMockup";

// Video URL for hero section, left side
// Woman using cellphone on couch, represents hands-free voice session with NIA (therapy-like feel)
const HERO_VIDEO = "https://videos.pexels.com/video-files/6608792/6608792-hd_1920_1080_25fps.mp4";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const phases = [
  {
    number: "01",
    name: "Reflect",
    subtitle: "Process what you're going through",
    description: "Nia, your dedicated guide, helps you decompress, journal, and build emotional readiness. Moving forward is accomplished best from a position of emotional clarity and stability.",
    features: [
      "Emotional processing support",
      "Guided journaling",
      "Build emotional readiness",
      "Decompress and reflect"
    ],
    lotusStage: "closed",
    role: "Emotional Readiness Guide"
  },
  {
    number: "02",
    name: "Discover",
    subtitle: "Explore who you could become",
    description: "Career discovery, strengths assessment, values alignment, and resource identification. Map out the possibilities that align with who you actually are.",
    features: [
      "Career path exploration",
      "Strengths assessment",
      "Values alignment",
      "Resource identification"
    ],
    lotusStage: "opening",
    role: "Career Discovery Coach"
  },
  {
    number: "03",
    name: "Become",
    subtitle: "Take action with coordinated support",
    description: "Your support team coordinates change management, resource navigation, and milestone tracking to help you achieve your goals.",
    features: [
      "Change management guidance",
      "Resource coordination",
      "Milestone tracking",
      "Coordinated support"
    ],
    lotusStage: "bloomed",
    role: "Transition Specialist"
  }
];

export default function HowItWorksPage() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <div className="overflow-hidden" ref={containerRef}>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center" data-testid="how-it-works-hero">
        {/* Video + Mockup Background */}
        <VideoMockupBackground
          videoSrc={HERO_VIDEO}
          MockupComponent={NiaMockup}
          overlay="bg-deep-violet/50"
        />

        {/* Floating accent elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
          <div className="absolute top-20 right-20 w-80 h-80 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-sm mb-6"
          >
            Your Transformation Journey
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="how-it-works-title"
          >
            How <span className="gradient-text">Become</span> Works
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            A journey designed around YOU, enabled by today's most advanced technology, and built for the day you won't need us anymore.
          </motion.p>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-warm-bone relative" data-testid="timeline-section">
        {/* Animated Timeline Line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 hidden lg:block">
          <div className="absolute inset-0 bg-gray-200" />
          <motion.div
            style={{ height: lineHeight }}
            className="absolute top-0 left-0 right-0 bg-gradient-to-b from-brand-peach to-brand-lavender"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {phases.map((phase, index) => (
            <motion.div
              key={phase.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className={`relative mb-32 last:mb-0 ${
                index % 2 === 0 ? "lg:pr-[55%]" : "lg:pl-[55%]"
              }`}
              data-testid={`phase-${phase.number}`}
            >
              {/* Phase Number Badge */}
              <div className="hidden lg:flex absolute left-1/2 top-0 -translate-x-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-brand-peach to-brand-lavender items-center justify-center z-10">
                <span className="text-deep-violet font-heading text-xl font-semibold">{phase.number}</span>
              </div>

              <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-lg border border-gray-100">
                {/* Mobile Phase Number */}
                <div className="lg:hidden mb-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-peach to-brand-lavender flex items-center justify-center">
                    <span className="text-deep-violet font-heading text-lg font-semibold">{phase.number}</span>
                  </div>
                  <span className="text-brand-primary/60 text-sm font-medium">{phase.role}</span>
                </div>

                <div className="flex flex-col lg:flex-row items-start gap-8">
                  {/* Lotus Animation */}
                  <div className="flex-shrink-0 mx-auto lg:mx-0">
                    <AnimatedLotus stage={phase.lotusStage} size={140} />
                  </div>

                  {/* Content */}
                  <div className="flex-grow text-center lg:text-left">
                    <div className="hidden lg:block">
                      <span className="text-brand-primary/60 text-sm font-medium">{phase.role}</span>
                    </div>
                    <h2 className="font-heading text-3xl text-text-dark mt-2 mb-2">
                      {phase.name}
                    </h2>
                    <p className="text-brand-peach font-medium mb-4">{phase.subtitle}</p>
                    <p className="text-text-dark/70 leading-relaxed mb-6">
                      {phase.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-3">
                      {phase.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-3 justify-center lg:justify-start">
                          <div className="w-5 h-5 rounded-full bg-brand-peach/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-brand-peach" />
                          </div>
                          <span className="text-text-dark/80 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Transition to Meet Your Team */}
      <section className="py-20 bg-gradient-to-b from-warm-bone to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-primary font-medium tracking-widest uppercase text-sm mb-4">
              Your Support System Awaits
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Meet the Team Behind Your Transformation
            </h2>
            <p className="text-text-dark/60 text-lg mb-8 max-w-2xl mx-auto">
              Four specialized support roles, led by Nia, your personal orchestrator who coordinates everything around you.
            </p>
            <Link to="/team">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="meet-team-transition-btn"
              >
                Meet Your Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA, Signup */}
      <section className="py-32 hero-dark" data-testid="how-it-works-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <div className="inline-block mb-8">
              <BecomeLogo size="large" />
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-text-light/70 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Join the waitlist and be among the first to experience transformation support that moves at your pace.
            </p>

            <Link to="/contact">
              <Button
                className="h-12 px-8 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-[0_0_30px_rgba(245,168,130,0.5)] transition-all hover:-translate-y-0.5"
                data-testid="signup-how-it-works-btn"
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
